import { PDFDocument, rgb } from 'pdf-lib';
import templateUrl from './template.pdf';
import { drawLogos } from "@/reports/shared_utils/drawLogos";
import { collectUnsupportedHighlights, drawUnsupportedHighlights } from "@/reports/shared_utils/highlightUnsupported";
import { sanitizeForPdf } from "@/reports/shared_utils/sanitizeForPdf";

const val = (v) => sanitizeForPdf(v);

// ── Preview/highlight maps ────────────────────────────────────────────────────

// Maps each schema key to one representative PDF AcroForm field name.
// Used by Page.jsx to locate the bounding box for the green highlight drawn
// over the last-changed field in the live preview pane.
export const SCHEMA_TO_PDF_FIELD = {
  // Student Info
  date:        'date',
  studentName: 'studentName1',
  oen:         'oen1',
  grade:       'grade1',
  teacher:     'teacher',

  // School Info
  board:     'board',
  school:    'school',
  address1:  'address1',
  address2:  'address2',
  principal: 'principal',
  telephone: 'telephone',

  // Attendance
  daysAbsent:        'daysAbsent',
  totalDaysAbsent:   'totalDaysAbsent',
  timesLate:         'timesLate',
  totalTimesLate:    'totalTimesLate',
  boardInformation1: 'boardInformation1',

  // Learning Skills
  responsibility:         'responsibility',
  organization:           'organization',
  independentWork:        'independentWork',
  collaboration:          'collaboration',
  initiative:             'initiative',
  selfRegulation:         'selfRegulation',
  // gr7-8 PDF field for LSWH textarea is "strengthsNextStepsLSWH" (not "strengthsNextSteps")
  strengthsNextStepsLSWH: 'strengthsNextStepsLSWH',

  // Subject results (point to the PWD checkbox as representative widget)
  languagePlan:   'languageESLELD',   languageResult:   'languagePWD',
  frenchPlan:     'frenchESLELD',     frenchResult:     'frenchPWD',
  nativeLanguage: 'nativeLanguage',
  nativeLanguagePlan:   'nativeLanguageESLELD',   nativeLanguageResult:   'nativeLanguagePWD',
  mathematicsPlan:      'mathematicsESLELD',      mathematicsResult:      'mathematicsPWD',
  scienceAndTechnologyPlan: 'scienceAndTechnologyESLELD', scienceAndTechnologyResult: 'scienceAndTechnologyPWD',
  historyPlan:   'historyESLELD',   historyResult:   'historyPWD',
  geographyPlan: 'geographyESLELD', geographyResult: 'geographyPWD',
  healthEducationPlan:   'healthEducationESLELD',   healthEducationResult:   'healthEducationPWD',
  physicalEducationPlan: 'physicalEducationESLELD', physicalEducationResult: 'physicalEducationPWD',

  // Arts
  artsHeader: 'artsHeader',
  arts1: 'arts1', arts1Plan: 'arts1ESLELD', arts1Result: 'arts1PWD',
  arts2: 'arts2', arts2Plan: 'arts2ESLELD', arts2Result: 'arts2PWD',
  arts3: 'arts3', arts3Plan: 'arts3ESLELD', arts3Result: 'arts3PWD',
  arts4: 'arts4', arts4Plan: 'arts4ESLELD', arts4Result: 'arts4PWD',

  // Optional Studies
  optionalStudies:       'optionalStudies',
  optionalStudiesPlan:   'optionalStudiesESLELD',
  optionalStudiesResult: 'optionalStudiesPWD',

  // Comments
  strengthsNextStepsSubjects: 'strengthsNextStepsSubjects',
  boardInformation2:          'boardInformation2',
};

// Schema keys whose PDF widgets appear on page 1 (all others are on page 2).
export const PAGE_1_SCHEMA_FIELDS = new Set([
  'date', 'studentName', 'oen', 'grade', 'teacher',
  'board', 'school', 'address1', 'address2', 'principal', 'telephone',
  'boardInformation1',
  'daysAbsent', 'totalDaysAbsent', 'timesLate', 'totalTimesLate',
  'responsibility', 'organization', 'independentWork',
  'collaboration', 'initiative', 'selfRegulation',
  'strengthsNextStepsLSWH',
]);

// One representative PDF field per step - used to compute the scroll position
// in the preview pane when the user navigates between steps.
export const STEP_ANCHOR_FIELDS = {
  1: 'studentName1',
  2: 'board',
  3: 'daysAbsent',
  4: 'responsibility',
  5: 'languagePWD',
  6: 'artsHeader',
};

// ── fill ──────────────────────────────────────────────────────────────────────

export const fill = async (formData, highlightRect = null, logos = []) => {
  const existingPdfBytes = await fetch(templateUrl).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const pdfForm = pdfDoc.getForm();

  let field;

  // ── Page 1: Student + School + Attendance ────────────────────────────────
  field = pdfForm.getField('date');
  field.setText(val(formData.date));

  field = pdfForm.getField('studentName1');
  field.setText(val(formData.studentName));
  field.setFontSize(8);

  field = pdfForm.getField('oen1');
  field.setText(val(formData.oen));
  field.setFontSize(8);

  field = pdfForm.getField('grade1');
  field.setText(val(formData.grade));
  field.setFontSize(8);

  field = pdfForm.getField('teacher');
  field.setText(val(formData.teacher));
  field.setFontSize(8);

  field = pdfForm.getField('board');
  field.setText(val(formData.board));
  field.setFontSize(8);

  field = pdfForm.getField('school');
  field.setText(val(formData.school));
  field.setFontSize(8);

  field = pdfForm.getField('address1');
  field.setText(val(formData.address1));
  field.setFontSize(8);

  field = pdfForm.getField('address2');
  field.setText(val(formData.address2));
  field.setFontSize(8);

  field = pdfForm.getField('principal');
  field.setText(val(formData.principal));
  field.setFontSize(8);

  field = pdfForm.getField('telephone');
  field.setText(val(formData.telephone));
  field.setFontSize(8);

  field = pdfForm.getField('daysAbsent');
  field.setText(val(formData.daysAbsent));
  field.setFontSize(8);

  field = pdfForm.getField('totalDaysAbsent');
  field.setText(val(formData.totalDaysAbsent));
  field.setFontSize(8);

  field = pdfForm.getField('timesLate');
  field.setText(val(formData.timesLate));
  field.setFontSize(8);

  field = pdfForm.getField('totalTimesLate');
  field.setText(val(formData.totalTimesLate));
  field.setFontSize(8);

  field = pdfForm.getField('boardInformation1');
  field.setText(val(formData.boardInformation1));
  field.setFontSize(8);

  // ── Page 1: Learning Skills & Work Habits ────────────────────────────────
  field = pdfForm.getField('responsibility');
  field.setText(val(formData.responsibility));
  field.setFontSize(8);

  field = pdfForm.getField('organization');
  field.setText(val(formData.organization));
  field.setFontSize(8);

  field = pdfForm.getField('independentWork');
  field.setText(val(formData.independentWork));
  field.setFontSize(8);

  field = pdfForm.getField('collaboration');
  field.setText(val(formData.collaboration));
  field.setFontSize(8);

  field = pdfForm.getField('initiative');
  field.setText(val(formData.initiative));
  field.setFontSize(8);

  field = pdfForm.getField('selfRegulation');
  field.setText(val(formData.selfRegulation));
  field.setFontSize(8);

  // gr7-8 uses "strengthsNextStepsLSWH" as the PDF field name (not "strengthsNextSteps")
  field = pdfForm.getField('strengthsNextStepsLSWH');
  field.setText(val(formData.strengthsNextStepsLSWH));
  field.setFontSize(8);
  field.enableMultiline();

  // ── Page 2 header (repeated student info) ────────────────────────────────
  field = pdfForm.getField('studentName2');
  field.setText(val(formData.studentName));
  field.setFontSize(9);

  field = pdfForm.getField('oen2');
  field.setText(val(formData.oen));
  field.setFontSize(9);

  field = pdfForm.getField('grade2');
  field.setText(val(formData.grade));
  field.setFontSize(9);

  // ── Page 2: Subject results ───────────────────────────────────────────────
  // Language
  pdfForm.getField('languagePWD')[formData.languageResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('languagePW') [formData.languageResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('languagePVW')[formData.languageResult === 'PVW' ? 'check' : 'uncheck']();

  // French
  pdfForm.getField('frenchPWD')[formData.frenchResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('frenchPW') [formData.frenchResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('frenchPVW')[formData.frenchResult === 'PVW' ? 'check' : 'uncheck']();

  // Native Language
  pdfForm.getField('nativeLanguage').setText(val(formData.nativeLanguage));
  pdfForm.getField('nativeLanguagePWD')[formData.nativeLanguageResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('nativeLanguagePW') [formData.nativeLanguageResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('nativeLanguagePVW')[formData.nativeLanguageResult === 'PVW' ? 'check' : 'uncheck']();

  // Mathematics
  pdfForm.getField('mathematicsPWD')[formData.mathematicsResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('mathematicsPW') [formData.mathematicsResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('mathematicsPVW')[formData.mathematicsResult === 'PVW' ? 'check' : 'uncheck']();

  // Science & Technology
  pdfForm.getField('scienceAndTechnologyPWD')[formData.scienceAndTechnologyResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('scienceAndTechnologyPW') [formData.scienceAndTechnologyResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('scienceAndTechnologyPVW')[formData.scienceAndTechnologyResult === 'PVW' ? 'check' : 'uncheck']();

  // History (replaces Social Studies in gr7-8)
  pdfForm.getField('historyPWD')[formData.historyResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('historyPW') [formData.historyResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('historyPVW')[formData.historyResult === 'PVW' ? 'check' : 'uncheck']();

  // Geography (replaces Social Studies in gr7-8)
  pdfForm.getField('geographyPWD')[formData.geographyResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('geographyPW') [formData.geographyResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('geographyPVW')[formData.geographyResult === 'PVW' ? 'check' : 'uncheck']();

  // Health Education
  pdfForm.getField('healthEducationPWD')[formData.healthEducationResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('healthEducationPW') [formData.healthEducationResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('healthEducationPVW')[formData.healthEducationResult === 'PVW' ? 'check' : 'uncheck']();

  // Physical Education
  pdfForm.getField('physicalEducationPWD')[formData.physicalEducationResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('physicalEducationPW') [formData.physicalEducationResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('physicalEducationPVW')[formData.physicalEducationResult === 'PVW' ? 'check' : 'uncheck']();

  // ── Arts ─────────────────────────────────────────────────────────────────
  field = pdfForm.getField('artsHeader');
  field.setText(val(formData.artsHeader));

  pdfForm.getField('arts1').setText(val(formData.arts1));
  pdfForm.getField('arts1PWD')[formData.arts1Result === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('arts1PW') [formData.arts1Result === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('arts1PVW')[formData.arts1Result === 'PVW' ? 'check' : 'uncheck']();

  pdfForm.getField('arts2').setText(val(formData.arts2));
  pdfForm.getField('arts2PWD')[formData.arts2Result === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('arts2PW') [formData.arts2Result === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('arts2PVW')[formData.arts2Result === 'PVW' ? 'check' : 'uncheck']();

  pdfForm.getField('arts3').setText(val(formData.arts3));
  pdfForm.getField('arts3PWD')[formData.arts3Result === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('arts3PW') [formData.arts3Result === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('arts3PVW')[formData.arts3Result === 'PVW' ? 'check' : 'uncheck']();

  pdfForm.getField('arts4').setText(val(formData.arts4));
  pdfForm.getField('arts4PWD')[formData.arts4Result === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('arts4PW') [formData.arts4Result === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('arts4PVW')[formData.arts4Result === 'PVW' ? 'check' : 'uncheck']();

  // Optional Studies
  pdfForm.getField('optionalStudies').setText(val(formData.optionalStudies));
  pdfForm.getField('optionalStudiesPWD')[formData.optionalStudiesResult === 'PWD' ? 'check' : 'uncheck']();
  pdfForm.getField('optionalStudiesPW') [formData.optionalStudiesResult === 'PW'  ? 'check' : 'uncheck']();
  pdfForm.getField('optionalStudiesPVW')[formData.optionalStudiesResult === 'PVW' ? 'check' : 'uncheck']();

  // Strengths & Next Steps - Subjects
  field = pdfForm.getField('strengthsNextStepsSubjects');
  field.setText(val(formData.strengthsNextStepsSubjects));
  field.setFontSize(8);
  field.enableMultiline();

  // Board Information - Page 2
  field = pdfForm.getField('boardInformation2');
  field.setText(val(formData.boardInformation2));
  field.setFontSize(8);

  // ── Plan checkboxes ───────────────────────────────────────────────────────
  // Language - twoPlans (ESLELD / IEP / NA)
  pdfForm.getField('languageESLELD')[formData.languagePlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('languageIEP')   [formData.languagePlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('languageNA')    [formData.languagePlan === 'NA'     ? 'check' : 'uncheck']();

  // French - sixPlans
  pdfForm.getField('frenchESLELD')   [formData.frenchPlan === 'ESLELD'    ? 'check' : 'uncheck']();
  pdfForm.getField('frenchIEP')      [formData.frenchPlan === 'IEP'       ? 'check' : 'uncheck']();
  pdfForm.getField('frenchNA')       [formData.frenchPlan === 'NA'        ? 'check' : 'uncheck']();
  pdfForm.getField('frenchCore')     [formData.frenchPlan === 'Core'      ? 'check' : 'uncheck']();
  pdfForm.getField('frenchImmersion')[formData.frenchPlan === 'Immersion' ? 'check' : 'uncheck']();
  pdfForm.getField('frenchExtended') [formData.frenchPlan === 'Extended'  ? 'check' : 'uncheck']();

  // Native Language - twoPlans (ESLELD / IEP / NA)
  pdfForm.getField('nativeLanguageESLELD')[formData.nativeLanguagePlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('nativeLanguageIEP')   [formData.nativeLanguagePlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('nativeLanguageNA')    [formData.nativeLanguagePlan === 'NA'     ? 'check' : 'uncheck']();

  // Mathematics - threePlans (ESLELD / IEP / French)
  pdfForm.getField('mathematicsESLELD')[formData.mathematicsPlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('mathematicsIEP')   [formData.mathematicsPlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('mathematicsFrench')[formData.mathematicsPlan === 'French' ? 'check' : 'uncheck']();

  // Science & Technology - threePlans
  pdfForm.getField('scienceAndTechnologyESLELD')[formData.scienceAndTechnologyPlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('scienceAndTechnologyIEP')   [formData.scienceAndTechnologyPlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('scienceAndTechnologyFrench')[formData.scienceAndTechnologyPlan === 'French' ? 'check' : 'uncheck']();

  // History - fourPlans (ESLELD / IEP / French / NA)
  pdfForm.getField('historyESLELD')[formData.historyPlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('historyIEP')   [formData.historyPlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('historyFrench')[formData.historyPlan === 'French' ? 'check' : 'uncheck']();
  pdfForm.getField('historyNA')    [formData.historyPlan === 'NA'     ? 'check' : 'uncheck']();

  // Geography - fourPlans
  pdfForm.getField('geographyESLELD')[formData.geographyPlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('geographyIEP')   [formData.geographyPlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('geographyFrench')[formData.geographyPlan === 'French' ? 'check' : 'uncheck']();
  pdfForm.getField('geographyNA')    [formData.geographyPlan === 'NA'     ? 'check' : 'uncheck']();

  // Health Education - threePlans
  pdfForm.getField('healthEducationESLELD')[formData.healthEducationPlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('healthEducationIEP')   [formData.healthEducationPlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('healthEducationFrench')[formData.healthEducationPlan === 'French' ? 'check' : 'uncheck']();

  // Physical Education - threePlans
  pdfForm.getField('physicalEducationESLELD')[formData.physicalEducationPlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('physicalEducationIEP')   [formData.physicalEducationPlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('physicalEducationFrench')[formData.physicalEducationPlan === 'French' ? 'check' : 'uncheck']();

  // Arts - fourPlans (ESLELD / IEP / French / NA)
  pdfForm.getField('arts1ESLELD')[formData.arts1Plan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('arts1IEP')   [formData.arts1Plan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('arts1French')[formData.arts1Plan === 'French' ? 'check' : 'uncheck']();
  pdfForm.getField('arts1NA')    [formData.arts1Plan === 'NA'     ? 'check' : 'uncheck']();

  pdfForm.getField('arts2ESLELD')[formData.arts2Plan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('arts2IEP')   [formData.arts2Plan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('arts2French')[formData.arts2Plan === 'French' ? 'check' : 'uncheck']();
  pdfForm.getField('arts2NA')    [formData.arts2Plan === 'NA'     ? 'check' : 'uncheck']();

  pdfForm.getField('arts3ESLELD')[formData.arts3Plan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('arts3IEP')   [formData.arts3Plan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('arts3French')[formData.arts3Plan === 'French' ? 'check' : 'uncheck']();
  pdfForm.getField('arts3NA')    [formData.arts3Plan === 'NA'     ? 'check' : 'uncheck']();

  pdfForm.getField('arts4ESLELD')[formData.arts4Plan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('arts4IEP')   [formData.arts4Plan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('arts4French')[formData.arts4Plan === 'French' ? 'check' : 'uncheck']();
  pdfForm.getField('arts4NA')    [formData.arts4Plan === 'NA'     ? 'check' : 'uncheck']();

  // Optional Studies - fourPlans
  pdfForm.getField('optionalStudiesESLELD')[formData.optionalStudiesPlan === 'ESLELD' ? 'check' : 'uncheck']();
  pdfForm.getField('optionalStudiesIEP')   [formData.optionalStudiesPlan === 'IEP'    ? 'check' : 'uncheck']();
  pdfForm.getField('optionalStudiesFrench')[formData.optionalStudiesPlan === 'French' ? 'check' : 'uncheck']();
  pdfForm.getField('optionalStudiesNA')    [formData.optionalStudiesPlan === 'NA'     ? 'check' : 'uncheck']();

  // teacherSignature and principalSignature are not fillable via AcroForm
  const __unsupportedHL = collectUnsupportedHighlights(pdfDoc, pdfForm);
  pdfForm.flatten();

  // ── Optional highlight rectangle drawn over last-changed field ───────────
  if (highlightRect) {
    const { pageIndex, x, y, width, height } = highlightRect;
    const pages = pdfDoc.getPages();
    if (pages[pageIndex]) {
      pages[pageIndex].drawRectangle({
        x: x - 2,
        y: y - 2,
        width: width + 4,
        height: height + 4,
        color: rgb(0, 0.95, 0),
        opacity: 0.25,
        borderColor: rgb(0.9, 0.7, 0),
        borderWidth: 0,
        borderOpacity: 0.9,
      });
    }
  }

  drawUnsupportedHighlights(pdfDoc, __unsupportedHL);

  // Class logos: drawn last so they sit on top of the flattened template.
  await drawLogos(pdfDoc, logos);

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
};
